import type { CloseGuideSource, OpenGuideSource } from "@expert/solve-tools";

import { noop } from "@mantine/core";
import { usePrevious } from "@mantine/hooks";
import { useEffect, useEffectEvent, useRef, useState } from "react";

import { isRightTopTab, type RightTopTab, sidecarTabEventBus } from "./sidecarTabEventBus";

interface UseRightTopTabStateParams {
    showAssistTab: boolean;
    customerSession: boolean;
    isVerified: boolean;
    showOffersTab: boolean;
    /** Whether any guide exists for this session — gates the Guided flows tab as the entry point. */
    areGuidesAvailable: boolean;
    /**
     * Whether a guide is currently open inside the Guided flows tab. This is the guide's open state,
     * not the tab's active state — a guide can be open while the expert is on another tab (off-screen)
     */
    isGuideOpen: boolean;
    /** Opens a guide when the expert clicks the Guided flows tab while none is open yet. */
    openGuide: (source: OpenGuideSource) => void;
    /**
     * Retracts the open guide when the expert navigates off the Guided flows tab. `isGuideOpen` tracks
     * whether a guide is currently open *inside* the Guided flows tab — independent of whether that tab
     * is the active one, so a guide can stay open (but off-screen) while the expert is on another tab.
     * Nothing else clears it on a tab switch, so without this it stays true after leaving, and the
     * recommendation auto-open (`useGuideRecommendation`) — which bails when a guide is already open —
     * never fires again.
     */
    closeGuide: (source: CloseGuideSource) => void;
    /** The guide renders inline in AI Assist instead of the Guided flows tab (`isAiAssistGuidedFlowEnabled`). */
    isGuideInline?: boolean;
    /**
     * Optional persistence adapter. When `initialTab` is provided (chat), it is restored on mount when
     * selectable, and the mount-time auto-select is suppressed so it doesn't overwrite the restored tab.
     * `onTabChange` reports the selected tab so the consumer can persist it. Voice omits both → ephemeral
     * local state, identical to the pre-hook behavior.
     */
    initialTab?: RightTopTab;
    onTabChange?: (tab: RightTopTab) => void;
}

interface UseRightTopTabStateResult {
    activeTab: RightTopTab;
    handleTabChange: (value: string | null) => void;
    /** Briefly true after Customer/Offers become enabled, to pulse the newly-available tabs. */
    tabsJustEnabled: boolean;
    /** True while a guide has auto-opened but the expert hasn't viewed the Guided flows tab yet. */
    hasGuideNotification: boolean;
}

/**
 * Owns the right-top tab selection: the default/auto-switch rules (customer → Customer, verify → AI
 * Assist), the imperative focus-bus, and — when a persistence adapter is passed — per-consumer restore
 * + write-back. Shared by voice (ephemeral) and chat (per-chat persisted).
 *
 * Guided flows is selectable whenever guides exist and opens one on click. A guide *auto-opening* brings
 * the expert to the Guided flows tab (product decision); a guide already open at first render (e.g. a chat
 * restoring a session mid-guide) instead lights a notification dot (`hasGuideNotification`) that clears
 * when the expert views the tab, so it can't clobber the restored/default tab. Post-verification a
 * confirmed customer with an open guide also lands on Guided flows.
 */
export function useSidecarTabState({
    showAssistTab,
    customerSession,
    isVerified,
    showOffersTab,
    areGuidesAvailable,
    isGuideOpen,
    openGuide,
    closeGuide,
    isGuideInline = false,
    initialTab,
    onTabChange = noop,
}: UseRightTopTabStateParams): UseRightTopTabStateResult {
    const isGuideInTab = isGuideOpen && !isGuideInline;

    // First *selectable* tab in priority order — the default and the fallback when the desired tab
    // isn't available, so we never land on a hidden/disabled tab. (Offers is subsumed by Customer.)
    const firstEnabledTab: RightTopTab = showAssistTab
        ? "ai-assist"
        : customerSession
          ? "customer"
          : isGuideInTab
            ? "guided-flows"
            : "ai-assist";

    const isTabSelectable = (tab: RightTopTab): boolean => {
        switch (tab) {
            case "ai-assist":
                return showAssistTab;
            case "customer":
                return customerSession;
            case "offers":
                return showOffersTab && customerSession && isVerified;
            case "guided-flows":
                return areGuidesAvailable && !isGuideInline;
        }
    };

    // Restore a persisted tab on mount when it's still selectable; otherwise fall back to the default.
    // While a valid tab is restored, suppress the mount-time auto-select effects so they don't overwrite
    // it. With no `initialTab` (voice) `restoredTab` is undefined → ref stays false → effects run as before.
    //
    // Assumes a saved tab's gating flags are already loaded (cached) by the time the tab exists — true for
    // in-memory persistence. If tabs ever move to durable storage, restore would need to retry once flags load.
    const restoredTab = initialTab && isTabSelectable(initialTab) ? initialTab : undefined;
    const [activeTab, setActiveTab] = useState<RightTopTab>(restoredTab ?? firstEnabledTab);
    const skipMountAutoSelectRef = useRef(restoredTab !== undefined);

    const previousCustomerSession = usePrevious(customerSession);
    const previousIsVerified = usePrevious(isVerified);
    const previousIsGuideOpen = usePrevious(isGuideOpen);
    const [tabsJustEnabled, setTabsJustEnabled] = useState(false);
    const [hasGuideNotification, setHasGuideNotification] = useState(false);

    useEffect(() => {
        if (previousCustomerSession === customerSession) return;
        if (skipMountAutoSelectRef.current) return;
        setActiveTab(customerSession ? "customer" : firstEnabledTab);
        if (customerSession && previousCustomerSession === false) {
            setTabsJustEnabled(true);
            const timer = setTimeout(() => setTabsJustEnabled(false), 800);
            return () => clearTimeout(timer);
        }
    }, [customerSession, previousCustomerSession, firstEnabledTab]);

    useEffect(() => {
        if (!isVerified || previousIsVerified) return;
        if (skipMountAutoSelectRef.current) return;
        // A confirmed customer with an open guide lands on Guided flows; otherwise the usual default.
        setActiveTab(isGuideInTab ? "guided-flows" : firstEnabledTab);
    }, [isVerified, previousIsVerified, isGuideInTab, firstEnabledTab]);

    // A guide auto-opening brings the expert to the Guided flows tab (product decision — reverses the
    // earlier "light a dot, don't steal the tab" behavior). Only on a real open transition: a guide already
    // open at first render (`previousIsGuideOpen === undefined`, e.g. a chat restoring mid-guide) instead
    // lights the notification dot and leaves the restored/default tab in place. Closing clears the dot and
    // drops back to the default if the expert was viewing Guided flows.
    //
    // TODO: the dot does not cover a guide that is ready to auto-open but held until customer verification
    // (the V2 gate in `shouldOpenActivationGuide`). Deliberately deferred: a dot before verification could draw
    // the expert to Guided flows before they verify the customer. Revisit if experts need that signal.
    useEffect(() => {
        if (previousIsGuideOpen === isGuideOpen) return;
        // Inline, a guide opening after verification brings the expert to AI Assist; unverified, the verify
        // effect above does, and one already open at first render keeps the restored/default tab.
        if (isGuideInline) {
            if (isGuideOpen && previousIsGuideOpen === false && showAssistTab && isVerified) setActiveTab("ai-assist");
            return;
        }
        if (isGuideOpen) {
            // `undefined` = the very first render, i.e. the guide was already open at mount rather than an
            // opening we witnessed (chat remounts per chat via `key`, so switching back to a chat can land
            // on an already-open guide). Only light the dot and keep the restored/default tab; a real
            // closed→open transition (previous === false) falls through to the tab switch below.
            if (previousIsGuideOpen === undefined) {
                setHasGuideNotification(true);
                return;
            }
            setActiveTab("guided-flows");
        } else {
            setHasGuideNotification(false);
            if (skipMountAutoSelectRef.current) return;
            setActiveTab((prev) => (prev === "guided-flows" ? firstEnabledTab : prev));
        }
    }, [isGuideOpen, previousIsGuideOpen, isGuideInline, showAssistTab, isVerified, firstEnabledTab]);

    // Viewing the Guided flows tab marks the auto-opened guide as seen.
    useEffect(() => {
        if (activeTab === "guided-flows") setHasGuideNotification(false);
    }, [activeTab]);

    // Clear the mount suppression after the first commit so later real transitions auto-switch.
    useEffect(() => {
        skipMountAutoSelectRef.current = false;
    }, []);

    // Let imperative actions outside this tree focus a tab — e.g. Quick rebuttal pushing a message
    // into the AI Assist timeline needs the AI Assist tab brought forward so the expert sees it.
    useEffect(() => sidecarTabEventBus.on("focus-tab", setActiveTab), []);

    // Report every change (clicks, auto-switch, focus-bus) to the persistence adapter. useEffectEvent
    // keeps onTabChange out of the deps so the effect fires only on a real tab change.
    const reportTabChange = useEffectEvent(onTabChange);
    useEffect(() => reportTabChange(activeTab), [activeTab]);

    const handleTabChange = (value: string | null) => {
        if (!isRightTopTab(value)) return;
        // Clicking Guided flows opens a guide when none is open yet (selection screen, or the lone guide).
        if (value === "guided-flows" && areGuidesAvailable && !isGuideOpen) {
            openGuide({ source: "manual", sourceDetails: "tab" });
        }
        // Leaving Guided flows retracts the open guide (see `closeGuide` above).
        if (activeTab === "guided-flows" && value !== "guided-flows" && isGuideOpen) {
            closeGuide("tabChange");
        }
        setActiveTab(value);
    };

    return { activeTab, handleTabChange, tabsJustEnabled, hasGuideNotification };
}
